import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Footer from "@/components/Footer";

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="px-6 md:px-12 lg:px-24 py-8 border-b border-gray-200">
        <div className="max-w-[1800px] mx-auto flex justify-between items-center">
          <Link to="/" className="font-['Syne'] font-extrabold text-2xl tracking-tight">
            CUE.PO
          </Link>
          <Link 
            to="/"
            className="flex items-center gap-2 text-gray-500 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-extrabold mb-8">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last updated: February 2025</p>

          <div className="prose prose-lg max-w-none space-y-8">
            <section>
              <h2 className="text-2xl font-bold mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-600">
                By accessing or using CUE.PO ("the Service"), you agree to be bound by these Terms of Service. 
                If you do not agree to these terms, please do not use our Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">2. Products and Services</h2>
              <p className="text-gray-600 mb-4">
                CUE.PO creates and sells custom printed posters featuring your personal goals. Our service includes:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Custom poster design generation based on your input</li>
                <li>Professional printing on premium matte paper</li>
                <li>Direct shipping to your specified address</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">3. Orders and Pricing</h2>
              <p className="text-gray-600 mb-4">
                All prices are displayed in USD and are subject to change without notice. Prices include the poster only; 
                shipping costs are calculated separately based on your location.
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Small (12×16 inches):</strong> $25</li>
                <li><strong>Medium (16×20 inches):</strong> $45</li>
                <li><strong>Large (20×30 inches):</strong> $65</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">4. Payment</h2>
              <p className="text-gray-600">
                All payments are processed securely through Stripe. By placing an order, you authorize us to charge 
                your payment method for the total order amount including shipping. We accept major credit cards, 
                debit cards, and other payment methods supported by Stripe.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">5. Shipping and Delivery</h2>
              <p className="text-gray-600 mb-4">
                Orders are fulfilled through our print-on-demand partner, Printful. Estimated delivery times:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><strong>Production:</strong> 2-5 business days</li>
                <li><strong>Shipping (US):</strong> 3-7 business days after production</li>
                <li><strong>International Shipping:</strong> 7-21 business days after production</li>
              </ul>
              <p className="text-gray-600 mt-4">
                Delivery times are estimates and may vary. You will receive tracking information once your order ships.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">6. Returns and Refunds</h2>
              <p className="text-gray-600 mb-4">
                Due to the custom nature of our products, we cannot accept returns or exchanges unless:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>The product arrives damaged or defective</li>
                <li>The product is significantly different from what was ordered</li>
                <li>The order was lost in transit</li>
              </ul>
              <p className="text-gray-600 mt-4">
                If you receive a damaged or incorrect item, please contact us within 14 days of delivery with 
                photos of the issue. We will arrange a replacement or refund at our discretion.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">7. Content Guidelines</h2>
              <p className="text-gray-600 mb-4">
                You are responsible for the goals and content you submit for your poster. You agree not to submit content that:
              </p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Infringes on any third-party intellectual property rights</li>
                <li>Contains hate speech, threats, or harassment</li>
                <li>Is illegal, obscene, or promotes illegal activities</li>
                <li>Contains personal information of others without consent</li>
              </ul>
              <p className="text-gray-600 mt-4">
                We reserve the right to refuse any order that violates these guidelines.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">8. Intellectual Property</h2>
              <p className="text-gray-600">
                The CUE.PO name, logo, and website design are our intellectual property. You retain ownership 
                of the goals you submit, but grant us a license to use them for order fulfillment. The poster 
                design template and layout are our intellectual property.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">9. Limitation of Liability</h2>
              <p className="text-gray-600">
                CUE.PO is provided "as is" without warranties of any kind. We are not liable for any indirect, 
                incidental, or consequential damages arising from your use of our service. Our total liability 
                is limited to the amount you paid for your order.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">10. Changes to Terms</h2>
              <p className="text-gray-600">
                We reserve the right to modify these Terms of Service at any time. Changes will be effective 
                immediately upon posting. Your continued use of the Service after changes constitutes acceptance 
                of the new terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold mb-4">11. Contact</h2>
              <p className="text-gray-600">
                For questions about these Terms of Service, please contact us at:{" "}
                <a href="mailto:legal@cue.po" className="text-[#002FA7] hover:underline">
                  legal@cue.po
                </a>
              </p>
            </section>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TermsOfService;
